// src/types/enums.ts
import { z } from "zod";
var Severity = /* @__PURE__ */ ((Severity2) => {
  Severity2["Critical"] = "critical";
  Severity2["Major"] = "major";
  Severity2["Minor"] = "minor";
  Severity2["Info"] = "info";
  return Severity2;
})(Severity || {});
var SeveritySchema = z.nativeEnum(Severity);
var ToolName = /* @__PURE__ */ ((ToolName2) => {
  ToolName2["PatternDetect"] = "pattern-detect";
  ToolName2["ContextAnalyzer"] = "context-analyzer";
  ToolName2["NamingConsistency"] = "naming-consistency";
  ToolName2["AiSignalClarity"] = "ai-signal-clarity";
  ToolName2["AgentGrounding"] = "agent-grounding";
  ToolName2["TestabilityIndex"] = "testability-index";
  ToolName2["DocDrift"] = "doc-drift";
  ToolName2["DependencyHealth"] = "dependency-health";
  ToolName2["ChangeAmplification"] = "change-amplification";
  ToolName2["CognitiveLoad"] = "cognitive-load";
  ToolName2["PatternEntropy"] = "pattern-entropy";
  ToolName2["ConceptCohesion"] = "concept-cohesion";
  ToolName2["SemanticDistance"] = "semantic-distance";
  ToolName2["ContractEnforcement"] = "contract-enforcement";
  ToolName2["HygieneAudit"] = "hygiene-audit";
  return ToolName2;
})(ToolName || {});
var ToolNameSchema = z.nativeEnum(ToolName);
var FRIENDLY_TOOL_NAMES = {
  ["pattern-detect" /* PatternDetect */]: "Semantic Duplicates",
  ["context-analyzer" /* ContextAnalyzer */]: "Context Fragmentation",
  ["naming-consistency" /* NamingConsistency */]: "Naming Consistency",
  ["ai-signal-clarity" /* AiSignalClarity */]: "AI Signal Clarity",
  ["agent-grounding" /* AgentGrounding */]: "Agent Grounding",
  ["testability-index" /* TestabilityIndex */]: "Testability Index",
  ["doc-drift" /* DocDrift */]: "Documentation Health",
  ["dependency-health" /* DependencyHealth */]: "Dependency Health",
  ["change-amplification" /* ChangeAmplification */]: "Change Amplification",
  ["cognitive-load" /* CognitiveLoad */]: "Cognitive Load",
  ["pattern-entropy" /* PatternEntropy */]: "Pattern Entropy",
  ["concept-cohesion" /* ConceptCohesion */]: "Concept Cohesion",
  ["semantic-distance" /* SemanticDistance */]: "Semantic Distance",
  ["contract-enforcement" /* ContractEnforcement */]: "Contract Enforcement",
  ["hygiene-audit" /* HygieneAudit */]: "Codebase Hygiene"
};
var IssueType = /* @__PURE__ */ ((IssueType2) => {
  IssueType2["DuplicatePattern"] = "duplicate-pattern";
  IssueType2["PatternInconsistency"] = "pattern-inconsistency";
  IssueType2["ContextFragmentation"] = "context-fragmentation";
  IssueType2["DependencyHealth"] = "dependency-health";
  IssueType2["CircularDependency"] = "circular-dependency";
  IssueType2["DocDrift"] = "doc-drift";
  IssueType2["NamingInconsistency"] = "naming-inconsistency";
  IssueType2["NamingQuality"] = "naming-quality";
  IssueType2["ArchitectureInconsistency"] = "architecture-inconsistency";
  IssueType2["DeadCode"] = "dead-code";
  IssueType2["MissingTypes"] = "missing-types";
  IssueType2["MagicLiteral"] = "magic-literal";
  IssueType2["BooleanTrap"] = "boolean-trap";
  IssueType2["ParameterObject"] = "parameter-object";
  IssueType2["AiSignalClarity"] = "ai-signal-clarity";
  IssueType2["LowTestability"] = "low-testability";
  IssueType2["AgentNavigationFailure"] = "agent-navigation-failure";
  IssueType2["AmbiguousApi"] = "ambiguous-api";
  IssueType2["ChangeAmplification"] = "change-amplification";
  IssueType2["ContractGap"] = "contract-gap";
  return IssueType2;
})(IssueType || {});
var IssueTypeSchema = z.nativeEnum(IssueType);
var AnalysisStatus = /* @__PURE__ */ ((AnalysisStatus2) => {
  AnalysisStatus2["Processing"] = "processing";
  AnalysisStatus2["Completed"] = "completed";
  AnalysisStatus2["Failed"] = "failed";
  return AnalysisStatus2;
})(AnalysisStatus || {});
var AnalysisStatusSchema = z.nativeEnum(AnalysisStatus);
var ModelTier = /* @__PURE__ */ ((ModelTier2) => {
  ModelTier2["Compact"] = "compact";
  ModelTier2["Standard"] = "standard";
  ModelTier2["Extended"] = "extended";
  ModelTier2["Frontier"] = "frontier";
  return ModelTier2;
})(ModelTier || {});
var ModelTierSchema = z.nativeEnum(ModelTier);

// src/types/common.ts
import { z as z2 } from "zod";
var LocationSchema = z2.object({
  file: z2.string(),
  line: z2.number(),
  column: z2.number().optional(),
  endLine: z2.number().optional(),
  endColumn: z2.number().optional()
});

// src/types/schemas/issue.ts
import { z as z3 } from "zod";
var IssueSchema = z3.object({
  type: IssueTypeSchema,
  severity: SeveritySchema,
  category: z3.string().optional(),
  message: z3.string(),
  location: LocationSchema,
  suggestion: z3.string().optional()
});

// src/types/schemas/metrics.ts
import { z as z4 } from "zod";
var MetricsSchema = z4.object({
  tokenCost: z4.number().optional(),
  complexityScore: z4.number().optional(),
  consistencyScore: z4.number().optional(),
  docFreshnessScore: z4.number().optional(),
  // AI agent readiness metrics (v0.12+)
  aiSignalClarityScore: z4.number().optional(),
  agentGroundingScore: z4.number().optional(),
  testabilityScore: z4.number().optional(),
  docDriftScore: z4.number().optional(),
  dependencyHealthScore: z4.number().optional(),
  modelContextTier: ModelTierSchema.optional(),
  // Business value metrics
  estimatedMonthlyCost: z4.number().optional(),
  estimatedDeveloperHours: z4.number().optional(),
  comprehensionDifficultyIndex: z4.number().optional(),
  // Extended metrics for specific spokes
  totalSymbols: z4.number().optional(),
  totalExports: z4.number().optional()
});

// src/types/schemas/report.ts
import { z as z5 } from "zod";
var AnalysisResultSchema = z5.object({
  fileName: z5.string(),
  issues: z5.array(IssueSchema),
  metrics: MetricsSchema
});
var SpokeSummarySchema = z5.object({
  totalFiles: z5.number().optional(),
  totalIssues: z5.number().optional(),
  criticalIssues: z5.number().optional(),
  majorIssues: z5.number().optional(),
  minorIssues: z5.number().optional(),
  score: z5.number().optional()
}).catchall(z5.any());
var SpokeOutputSchema = z5.object({
  results: z5.array(AnalysisResultSchema),
  summary: SpokeSummarySchema,
  metadata: z5.object({
    toolName: z5.string(),
    version: z5.string().optional(),
    timestamp: z5.string().optional(),
    config: z5.any().optional()
  }).catchall(z5.any()).optional()
});
var UnifiedReportSchema = z5.object({
  summary: z5.object({
    totalFiles: z5.number(),
    totalIssues: z5.number(),
    criticalIssues: z5.number(),
    majorIssues: z5.number(),
    minorIssues: z5.number(),
    businessImpact: z5.object({
      estimatedMonthlyWaste: z5.number().optional(),
      potentialSavings: z5.number().optional(),
      productivityHours: z5.number().optional()
    }).optional()
  }),
  results: z5.array(AnalysisResultSchema),
  scoring: z5.object({
    overall: z5.number(),
    rating: z5.string(),
    timestamp: z5.string(),
    breakdown: z5.array(
      z5.object({
        toolName: z5.union([ToolNameSchema, z5.string()]),
        score: z5.number()
      }).catchall(z5.any())
    )
  }).optional()
}).catchall(z5.any());

// src/types/schemas/config.ts
import { z as z6 } from "zod";
var AutoExcludeSchema = z6.object({
  /** Enable automatic detection of test files */
  tests: z6.boolean().optional(),
  /** Enable automatic detection of mock files */
  mocks: z6.boolean().optional(),
  /** Enable automatic detection of barrel/re-export files */
  barrels: z6.boolean().optional(),
  /** Enable automatic detection of generated files */
  generated: z6.boolean().optional()
}).optional();
var TieredExcludeSchema = z6.record(z6.string(), z6.array(z6.string()));
var AIReadyConfigSchema = z6.object({
  /** Extend from another config file (relative path) */
  extends: z6.string().optional(),
  /** Files or directories to exclude from scan (flat array or tiered) */
  exclude: z6.union([z6.array(z6.string()), TieredExcludeSchema]).optional(),
  /** Auto-exclusion settings for common patterns */
  autoExclude: AutoExcludeSchema,
  /** Fail CI/CD if score below threshold (0-100) */
  threshold: z6.number().optional(),
  /** Fail on issues: critical, major, any */
  failOn: z6.enum(["critical", "major", "any", "none"]).optional(),
  /** Scan-specific configuration */
  scan: z6.object({
    include: z6.array(z6.string()).optional(),
    exclude: z6.array(z6.string()).optional(),
    parallel: z6.boolean().optional(),
    deep: z6.boolean().optional(),
    tools: z6.array(z6.string()).optional()
  }).optional(),
  /** Output-specific configuration */
  output: z6.object({
    /** Output format (json, console, html) */
    format: z6.enum(["json", "console", "html"]).optional(),
    /** Output file path */
    path: z6.string().optional(),
    /** Output directory */
    saveTo: z6.string().optional(),
    /** Whether to show score breakdown in console */
    showBreakdown: z6.boolean().optional(),
    /** Baseline report to compare against */
    compareBaseline: z6.string().optional()
  }).optional(),
  /** Tool-specific configuration overrides (Strictly ToolName -> Config) */
  tools: z6.record(
    z6.string(),
    z6.object({
      /** Whether to enable this tool */
      enabled: z6.boolean().optional(),
      /** Severity overrides for specific categories */
      severityOverrides: z6.record(z6.string(), SeveritySchema).optional()
    }).catchall(z6.any())
  ).optional(),
  /** Scoring profile and weights */
  scoring: z6.object({
    /** Name of the scoring profile (e.g. "strict", "balanced") */
    profile: z6.string().optional(),
    /** Custom weights for tools and metrics */
    weights: z6.record(z6.string(), z6.number()).optional()
  }).optional(),
  /** Visualizer settings (interactive graph) */
  visualizer: z6.object({
    groupingDirs: z6.array(z6.string()).optional(),
    graph: z6.object({
      maxNodes: z6.number().optional(),
      maxEdges: z6.number().optional()
    }).optional()
  }).optional()
}).catchall(z6.any());

// src/types/business.ts
import { z as z7 } from "zod";
var LeadSource = /* @__PURE__ */ ((LeadSource2) => {
  LeadSource2["ClawMoreHero"] = "clawmore-hero";
  LeadSource2["ClawMoreWaitlist"] = "clawmore-waitlist";
  LeadSource2["ClawMoreBeta"] = "clawmore-beta";
  LeadSource2["AiReadyPlatform"] = "aiready-platform";
  return LeadSource2;
})(LeadSource || {});
var LeadSourceSchema = z7.nativeEnum(LeadSource);
var LeadSchema = z7.object({
  id: z7.string(),
  email: z7.string().email(),
  name: z7.string().min(1),
  interest: z7.string().default("General"),
  notes: z7.string().optional(),
  timestamp: z7.string().datetime(),
  source: LeadSourceSchema,
  status: z7.enum(["new", "contacted", "qualified", "converted", "archived"]).default("new")
});
var LeadSubmissionSchema = LeadSchema.omit({
  id: true,
  timestamp: true,
  status: true
});
var ManagedAccountSchema = z7.object({
  id: z7.string(),
  // Internal UUID
  accountId: z7.string(),
  // AWS Account ID
  userId: z7.string(),
  // Owner (team@getaiready.dev)
  stripeSubscriptionId: z7.string(),
  // AI Token Management
  tokenStrategy: z7.enum(["managed", "byok"]).default("managed"),
  byokConfig: z7.object({
    openaiKey: z7.string().optional(),
    anthropicKey: z7.string().optional(),
    openrouterKey: z7.string().optional()
  }).optional(),
  // Financials (in cents)
  baseFeeCents: z7.number().default(2900),
  includedComputeCents: z7.number().default(1500),
  // $15.00 AWS included
  includedTokenCents: z7.number().default(500),
  // $5.00 Managed Tokens included
  // Pre-paid Balance (credits)
  prepaidTokenBalanceCents: z7.number().default(0),
  // Users buy these in $10 packs
  currentMonthlyTokenSpendCents: z7.number().default(0),
  // Governance
  status: z7.enum(["provisioning", "active", "warning", "quarantined", "suspended"]).default("provisioning"),
  lastCostSyncAt: z7.string().datetime().optional(),
  region: z7.string().default("ap-southeast-2"),
  // Alerting thresholds (percentage of includedComputeCents)
  alertThresholds: z7.array(z7.number()).default([50, 80, 100, 150])
});

// src/types.ts
var GLOBAL_INFRA_OPTIONS = [
  "rootDir",
  "include",
  "exclude",
  "tools",
  "scoring",
  "changedFilesOnly"
];
var GLOBAL_SCAN_OPTIONS = [
  "rootDir",
  "include",
  "exclude",
  "config",
  "threshold",
  "output",
  "format",
  "parallel",
  "changedFilesOnly"
];
var COMMON_FINE_TUNING_OPTIONS = [
  "maxDepth",
  "minSimilarity",
  "threshold",
  "showBreakdown"
];

// src/utils/visualization.ts
function generateHTML(graph) {
  const payload = JSON.stringify(graph, null, 2);
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>AIReady Visualization</title>
    <style>
      html,body { height: 100%; margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: #0f172a; color: #e2e8f0 }
      #container { display:flex; height:100vh }
      #panel { width: 320px; padding: 16px; background: #071130; box-shadow: -2px 0 8px rgba(0,0,0,0.3); overflow:auto }
      #canvasWrap { flex:1; display:flex; align-items:center; justify-content:center }
      canvas { background: #0b1220; border-radius:8px }
      .stat { margin-bottom:12px }
    </style>
  </head>
  <body>
    <div id="container">
      <div id="canvasWrap"><canvas id="canvas" width="1200" height="800"></canvas></div>
      <div id="panel">
        <h2>AIReady Visualization</h2>
        <div class="stat"><strong>Files:</strong> <span id="stat-files"></span></div>
        <div class="stat"><strong>Dependencies:</strong> <span id="stat-deps"></span></div>
        <div class="stat"><strong>Legend</strong></div>
        <div style="font-size:13px;line-height:1.3;color:#cbd5e1;margin-top:8px">
          <div style="margin-bottom:8px"><span style="display:inline-block;width:12px;height:12px;background:#ff4d4f;margin-right:8px;border:1px solid rgba(255,255,255,0.06)"></span><strong>Critical</strong>: highest severity issues.</div>
          <div style="margin-bottom:8px"><span style="display:inline-block;width:12px;height:12px;background:#ff9900;margin-right:8px;border:1px solid rgba(255,255,255,0.06)"></span><strong>Major</strong>: important issues.</div>
          <div style="margin-bottom:8px"><span style="display:inline-block;width:12px;height:12px;background:#ffd666;margin-right:8px;border:1px solid rgba(255,255,255,0.06)"></span><strong>Minor</strong>: low priority issues.</div>
          <div style="margin-bottom:8px"><span style="display:inline-block;width:12px;height:12px;background:#91d5ff;margin-right:8px;border:1px solid rgba(255,255,255,0.06)"></span><strong>Info</strong>: informational notes.</div>
          <div style="margin-top:10px;color:#94a3b8"><strong>Node size</strong>: larger = higher token cost, more issues or dependency weight.</div>
          <div style="margin-top:6px;color:#94a3b8"><strong>Proximity</strong>: nodes that are spatially close are more contextually related; relatedness is represented by distance and size rather than explicit edges.</div>
          <div style="margin-top:6px;color:#94a3b8"><strong>Edge colors</strong>: <span style="color:#fb7e81">Similarity</span>, <span style="color:#84c1ff">Dependency</span>, <span style="color:#ffa500">Reference</span>, default <span style="color:#334155">Other</span>.</div>
        </div>
      </div>
    </div>

    <script>
      const graphData = ${payload};
      document.getElementById('stat-files').textContent = graphData.metadata.totalFiles;
      document.getElementById('stat-deps').textContent = graphData.metadata.totalDependencies;

      const canvas = document.getElementById('canvas');
      const ctx = canvas.getContext('2d');

      const nodes = graphData.nodes.map((n, i) => ({
        ...n,
        x: canvas.width / 2 + Math.cos(i / graphData.nodes.length * Math.PI * 2) * (Math.min(canvas.width, canvas.height) / 3),
        y: canvas.height / 2 + Math.sin(i / graphData.nodes.length * Math.PI * 2) * (Math.min(canvas.width, canvas.height) / 3),
      }));

      function draw() {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        graphData.edges.forEach(edge => {
          const s = nodes.find(n => n.id === edge.source);
          const t = nodes.find(n => n.id === edge.target);
          if (!s || !t) return;
          if (edge.type === 'related') return;
          if (edge.type === 'similarity') {
            ctx.strokeStyle = '#fb7e81';
            ctx.lineWidth = 1.2;
          } else if (edge.type === 'dependency') {
            ctx.strokeStyle = '#84c1ff';
            ctx.lineWidth = 1.0;
          } else if (edge.type === 'reference') {
            ctx.strokeStyle = '#ffa500';
            ctx.lineWidth = 0.9;
          } else {
            ctx.strokeStyle = '#334155';
            ctx.lineWidth = 0.8;
          }
          ctx.beginPath();
          ctx.moveTo(s.x, s.y);
          ctx.lineTo(t.x, t.y);
          ctx.stroke();
        });

        const groups = {};
        nodes.forEach(n => {
          const g = n.group || '__default';
          if (!groups[g]) groups[g] = { minX: n.x, minY: n.y, maxX: n.x, maxY: n.y };
          groups[g].minX = Math.min(groups[g].minX, n.x);
          groups[g].minY = Math.min(groups[g].minY, n.y);
          groups[g].maxX = Math.max(groups[g].maxX, n.x);
          groups[g].maxY = Math.max(groups[g].maxY, n.y);
        });

        const groupRelations = {};
        graphData.edges.forEach(edge => {
          const sNode = nodes.find(n => n.id === edge.source);
          const tNode = nodes.find(n => n.id === edge.target);
          if (!sNode || !tNode) return;
          const g1 = sNode.group || '__default';
          const g2 = tNode.group || '__default';
          if (g1 === g2) return;
          const key = g1 < g2 ? g1 + '::' + g2 : g2 + '::' + g1;
          groupRelations[key] = (groupRelations[key] || 0) + 1;
        });

        Object.keys(groupRelations).forEach(k => {
          const count = groupRelations[k];
          const [ga, gb] = k.split('::');
          if (!groups[ga] || !groups[gb]) return;
          const ax = (groups[ga].minX + groups[ga].maxX) / 2;
          const ay = (groups[ga].minY + groups[ga].maxY) / 2;
          const bx = (groups[gb].minX + groups[gb].maxX) / 2;
          const by = (groups[gb].minY + groups[gb].maxY) / 2;
          ctx.beginPath();
          ctx.strokeStyle = 'rgba(148,163,184,0.25)';
          ctx.lineWidth = Math.min(6, 0.6 + Math.sqrt(count));
          ctx.moveTo(ax, ay);
          ctx.lineTo(bx, by);
          ctx.stroke();
        });

        Object.keys(groups).forEach(g => {
          if (g === '__default') return;
          const box = groups[g];
          const pad = 16;
          const x = box.minX - pad;
          const y = box.minY - pad;
          const w = (box.maxX - box.minX) + pad * 2;
          const h = (box.maxY - box.minY) + pad * 2;
          ctx.save();
          ctx.fillStyle = 'rgba(30,64,175,0.04)';
          ctx.strokeStyle = 'rgba(30,64,175,0.12)';
          ctx.lineWidth = 1.2;
          const r = 8;
          ctx.beginPath();
          ctx.moveTo(x + r, y);
          ctx.arcTo(x + w, y, x + w, y + h, r);
          ctx.arcTo(x + w, y + h, x, y + h, r);
          ctx.arcTo(x, y + h, x, y, r);
          ctx.arcTo(x, y, x + w, y, r);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.restore();
          ctx.fillStyle = '#94a3b8';
          ctx.font = '11px sans-serif';
          ctx.fillText(g, x + 8, y + 14);
        });

        nodes.forEach(n => {
          const sizeVal = (n.size || n.value || 1);
          const r = 6 + (sizeVal / 2);
          ctx.beginPath();
          ctx.fillStyle = n.color || '#60a5fa';
          ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = '#e2e8f0';
          ctx.font = '11px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(n.label || n.id.split('/').slice(-1)[0], n.x, n.y + r + 12);
        });
      }

      draw();
    </script>
  </body>
</html>`;
}

// src/utils/rating-helpers.ts
var ReadinessRating = /* @__PURE__ */ ((ReadinessRating2) => {
  ReadinessRating2["Excellent"] = "Excellent";
  ReadinessRating2["Good"] = "Good";
  ReadinessRating2["Fair"] = "Fair";
  ReadinessRating2["NeedsWork"] = "Needs Work";
  ReadinessRating2["Critical"] = "Critical";
  return ReadinessRating2;
})(ReadinessRating || {});
function getRatingMetadata(score) {
  if (score >= 90) {
    return {
      label: "Excellent",
      slug: "excellent",
      emoji: "\u2705",
      rating: "Excellent" /* Excellent */
    };
  }
  if (score >= 75) {
    return {
      label: "Good",
      slug: "good",
      emoji: "\u{1F44D}",
      rating: "Good" /* Good */
    };
  }
  if (score >= 60) {
    return {
      label: "Fair",
      slug: "fair",
      emoji: "\u{1F44C}",
      rating: "Fair" /* Fair */
    };
  }
  if (score >= 40) {
    return {
      label: "Needs Work",
      slug: "needs-work",
      emoji: "\u{1F528}",
      rating: "Needs Work" /* NeedsWork */
    };
  }
  return {
    label: "Critical",
    slug: "critical",
    emoji: "\u{1F6A8}",
    rating: "Critical" /* Critical */
  };
}
function getRatingLabel(score) {
  return getRatingMetadata(score).label;
}
function getRatingSlug(score) {
  return getRatingMetadata(score).slug;
}
function getRatingEmoji(score) {
  return getRatingMetadata(score).emoji;
}
function getToolEmoji(score) {
  return getRatingEmoji(score);
}
function getPriorityIcon(priority) {
  switch (priority) {
    case "critical":
      return "\u{1F534}";
    case "high":
      return "\u{1F7E0}";
    case "medium":
      return "\u{1F7E1}";
    case "low":
      return "\u{1F535}";
    default:
      return "\u26AA";
  }
}
function getRating(score) {
  return getRatingMetadata(score).rating;
}

// src/scoring-types.ts
var RecommendationPriority = /* @__PURE__ */ ((RecommendationPriority2) => {
  RecommendationPriority2["High"] = "high";
  RecommendationPriority2["Medium"] = "medium";
  RecommendationPriority2["Low"] = "low";
  return RecommendationPriority2;
})(RecommendationPriority || {});

// src/scoring.ts
var SCORING_CONSTANTS = {
  WEIGHTS: {
    DEFAULT: 5,
    MAX: 100
  },
  THRESHOLDS: {
    XS: 80,
    SMALL: 75,
    MEDIUM: 70,
    LARGE: 65,
    ENTERPRISE: 58,
    NORMALIZATION_OFFSET: 70
  },
  FILE_COUNTS: {
    XS: 50,
    SMALL: 200,
    MEDIUM: 500,
    LARGE: 2e3
  },
  DELIMITERS: {
    LIST: ",",
    PAIR: ":",
    FORMULA_JOIN: " + "
  }
};
var DEFAULT_TOOL_WEIGHTS = {
  ["pattern-detect" /* PatternDetect */]: 22,
  ["context-analyzer" /* ContextAnalyzer */]: 19,
  ["naming-consistency" /* NamingConsistency */]: 14,
  ["ai-signal-clarity" /* AiSignalClarity */]: 11,
  ["agent-grounding" /* AgentGrounding */]: 10,
  ["testability-index" /* TestabilityIndex */]: 10,
  ["doc-drift" /* DocDrift */]: 8,
  ["dependency-health" /* DependencyHealth */]: 6,
  ["change-amplification" /* ChangeAmplification */]: 8,
  ["contract-enforcement" /* ContractEnforcement */]: 10,
  ["cognitive-load" /* CognitiveLoad */]: 8,
  ["pattern-entropy" /* PatternEntropy */]: 8,
  ["concept-cohesion" /* ConceptCohesion */]: 10,
  ["semantic-distance" /* SemanticDistance */]: 10,
  ["hygiene-audit" /* HygieneAudit */]: 10
};
var TOOL_NAME_MAP = {
  patterns: "pattern-detect" /* PatternDetect */,
  "pattern-detect": "pattern-detect" /* PatternDetect */,
  context: "context-analyzer" /* ContextAnalyzer */,
  "context-analyzer": "context-analyzer" /* ContextAnalyzer */,
  consistency: "naming-consistency" /* NamingConsistency */,
  namingconsistency: "naming-consistency" /* NamingConsistency */,
  "naming-consistency": "naming-consistency" /* NamingConsistency */,
  "ai-signal": "ai-signal-clarity" /* AiSignalClarity */,
  "ai-signal-clarity": "ai-signal-clarity" /* AiSignalClarity */,
  grounding: "agent-grounding" /* AgentGrounding */,
  "agent-grounding": "agent-grounding" /* AgentGrounding */,
  testability: "testability-index" /* TestabilityIndex */,
  "testability-index": "testability-index" /* TestabilityIndex */,
  "doc-drift": "doc-drift" /* DocDrift */,
  "deps-health": "dependency-health" /* DependencyHealth */,
  "dependency-health": "dependency-health" /* DependencyHealth */,
  "change-amp": "change-amplification" /* ChangeAmplification */,
  "change-amplification": "change-amplification" /* ChangeAmplification */,
  contract: "contract-enforcement" /* ContractEnforcement */,
  "contract-enforcement": "contract-enforcement" /* ContractEnforcement */
};
var ScoringProfile = /* @__PURE__ */ ((ScoringProfile2) => {
  ScoringProfile2["Default"] = "default";
  ScoringProfile2["Agentic"] = "agentic";
  ScoringProfile2["Logic"] = "logic";
  ScoringProfile2["UI"] = "ui";
  ScoringProfile2["Cost"] = "cost";
  ScoringProfile2["Security"] = "security";
  return ScoringProfile2;
})(ScoringProfile || {});
var SCORING_PROFILES = {
  ["default" /* Default */]: DEFAULT_TOOL_WEIGHTS,
  ["agentic" /* Agentic */]: {
    ["ai-signal-clarity" /* AiSignalClarity */]: 25,
    ["agent-grounding" /* AgentGrounding */]: 25,
    ["testability-index" /* TestabilityIndex */]: 15,
    ["contract-enforcement" /* ContractEnforcement */]: 15,
    ["hygiene-audit" /* HygieneAudit */]: 10,
    ["context-analyzer" /* ContextAnalyzer */]: 10,
    ["naming-consistency" /* NamingConsistency */]: 10
  },
  ["logic" /* Logic */]: {
    ["testability-index" /* TestabilityIndex */]: 40,
    ["naming-consistency" /* NamingConsistency */]: 20,
    ["context-analyzer" /* ContextAnalyzer */]: 20,
    ["hygiene-audit" /* HygieneAudit */]: 15,
    ["pattern-detect" /* PatternDetect */]: 10,
    ["change-amplification" /* ChangeAmplification */]: 10
  },
  ["ui" /* UI */]: {
    ["naming-consistency" /* NamingConsistency */]: 30,
    ["context-analyzer" /* ContextAnalyzer */]: 30,
    ["pattern-detect" /* PatternDetect */]: 20,
    ["doc-drift" /* DocDrift */]: 10,
    ["ai-signal-clarity" /* AiSignalClarity */]: 10
  },
  ["cost" /* Cost */]: {
    ["pattern-detect" /* PatternDetect */]: 50,
    ["context-analyzer" /* ContextAnalyzer */]: 30,
    ["change-amplification" /* ChangeAmplification */]: 10,
    ["dependency-health" /* DependencyHealth */]: 10
  },
  ["security" /* Security */]: {
    ["naming-consistency" /* NamingConsistency */]: 30,
    ["testability-index" /* TestabilityIndex */]: 25,
    ["contract-enforcement" /* ContractEnforcement */]: 15,
    ["dependency-health" /* DependencyHealth */]: 20,
    ["context-analyzer" /* ContextAnalyzer */]: 10
  }
};
var CONTEXT_TIER_THRESHOLDS = {
  compact: { idealTokens: 3e3, criticalTokens: 1e4, idealDepth: 4 },
  standard: { idealTokens: 5e3, criticalTokens: 15e3, idealDepth: 5 },
  extended: { idealTokens: 15e3, criticalTokens: 5e4, idealDepth: 7 },
  frontier: { idealTokens: 5e4, criticalTokens: 15e4, idealDepth: 10 }
};
var SIZE_ADJUSTED_THRESHOLDS = {
  xs: SCORING_CONSTANTS.THRESHOLDS.XS,
  // < 50 files
  small: SCORING_CONSTANTS.THRESHOLDS.SMALL,
  // 50-200 files
  medium: SCORING_CONSTANTS.THRESHOLDS.MEDIUM,
  // 200-500 files
  large: SCORING_CONSTANTS.THRESHOLDS.LARGE,
  // 500-2000 files
  enterprise: SCORING_CONSTANTS.THRESHOLDS.ENTERPRISE
  // 2000+ files
};
function getProjectSizeTier(fileCount) {
  if (fileCount < SCORING_CONSTANTS.FILE_COUNTS.XS) return "xs";
  if (fileCount < SCORING_CONSTANTS.FILE_COUNTS.SMALL) return "small";
  if (fileCount < SCORING_CONSTANTS.FILE_COUNTS.MEDIUM) return "medium";
  if (fileCount < SCORING_CONSTANTS.FILE_COUNTS.LARGE) return "large";
  return "enterprise";
}
function getRecommendedThreshold(fileCount, modelTier = "standard") {
  const sizeTier = getProjectSizeTier(fileCount);
  const base = SIZE_ADJUSTED_THRESHOLDS[sizeTier];
  const modelBonus = modelTier === "frontier" ? -3 : modelTier === "extended" ? -2 : 0;
  return base + modelBonus;
}
function normalizeToolName(shortName) {
  return TOOL_NAME_MAP[shortName.toLowerCase()] ?? shortName;
}
function getToolWeight(toolName, toolConfig, cliOverride, profile = "default" /* Default */) {
  if (cliOverride !== void 0) return cliOverride;
  if (toolConfig?.scoreWeight !== void 0) return toolConfig.scoreWeight;
  const profileWeights = SCORING_PROFILES[profile] ?? DEFAULT_TOOL_WEIGHTS;
  return profileWeights[toolName] ?? DEFAULT_TOOL_WEIGHTS[toolName] ?? SCORING_CONSTANTS.WEIGHTS.DEFAULT;
}
function parseWeightString(weightStr) {
  const weights = /* @__PURE__ */ new Map();
  if (!weightStr) return weights;
  const pairs = weightStr.split(SCORING_CONSTANTS.DELIMITERS.LIST);
  for (const pair of pairs) {
    const [toolShortName, weightValueStr] = pair.split(
      SCORING_CONSTANTS.DELIMITERS.PAIR
    );
    if (toolShortName && weightValueStr) {
      const toolName = normalizeToolName(toolShortName.trim());
      const weight = parseInt(weightValueStr.trim(), 10);
      if (!isNaN(weight) && weight > 0) {
        weights.set(toolName, weight);
      }
    }
  }
  return weights;
}
function calculateOverallScore(toolOutputs, config, cliWeights) {
  if (toolOutputs.size === 0) {
    throw new Error("No tool outputs provided for scoring");
  }
  const profile = config?.profile || "default" /* Default */;
  const weights = /* @__PURE__ */ new Map();
  for (const [toolName] of toolOutputs.entries()) {
    const cliWeight = cliWeights?.get(toolName);
    const configWeight = config?.tools?.[toolName]?.scoreWeight;
    const weight = cliWeight ?? configWeight ?? getToolWeight(toolName, void 0, void 0, profile);
    weights.set(toolName, weight);
  }
  let weightedSum = 0;
  let totalWeight = 0;
  const breakdown = [];
  const toolsUsed = [];
  const calculationWeights = {};
  for (const [toolName, output] of toolOutputs.entries()) {
    const weight = weights.get(toolName) ?? SCORING_CONSTANTS.WEIGHTS.DEFAULT;
    weightedSum += output.score * weight;
    totalWeight += weight;
    toolsUsed.push(toolName);
    calculationWeights[toolName] = weight;
    breakdown.push(output);
  }
  const overall = Math.round(weightedSum / totalWeight);
  const rating = getRating(overall);
  const formulaParts = Array.from(toolOutputs.entries()).map(
    ([name, output]) => {
      const weight = weights.get(name) ?? SCORING_CONSTANTS.WEIGHTS.DEFAULT;
      return `(${output.score} \xD7 ${weight})`;
    }
  );
  const formulaStr = `[${formulaParts.join(SCORING_CONSTANTS.DELIMITERS.FORMULA_JOIN)}] / ${totalWeight} = ${overall}`;
  return {
    overall,
    rating,
    timestamp: (/* @__PURE__ */ new Date()).toISOString(),
    toolsUsed,
    breakdown,
    calculation: {
      formula: formulaStr,
      weights: calculationWeights,
      normalized: formulaStr
    }
  };
}
function getRatingWithContext(score, fileCount, modelTier = "standard") {
  const threshold = getRecommendedThreshold(fileCount, modelTier);
  const normalized = score - threshold + SCORING_CONSTANTS.THRESHOLDS.NORMALIZATION_OFFSET;
  return getRating(normalized);
}
function getRatingDisplay(rating) {
  switch (rating) {
    case "Excellent" /* Excellent */:
      return { emoji: "\u2705", color: "green" };
    case "Good" /* Good */:
      return { emoji: "\u{1F44D}", color: "blue" };
    case "Fair" /* Fair */:
      return { emoji: "\u26A0\uFE0F", color: "yellow" };
    case "Needs Work" /* NeedsWork */:
      return { emoji: "\u{1F528}", color: "orange" };
    case "Critical" /* Critical */:
      return { emoji: "\u274C", color: "red" };
    default:
      return { emoji: "\u2753", color: "gray" };
  }
}
function formatScore(result) {
  const { emoji } = getRatingDisplay(result.rating);
  return `${result.overall}/100 (${result.rating}) ${emoji}`;
}
function formatToolScore(output) {
  let result = `  Score: ${output.score}/100

`;
  if (output.factors && output.factors.length > 0) {
    result += `  Factors:
`;
    output.factors.forEach((factor) => {
      const impactSign = factor.impact > 0 ? "+" : "";
      result += `    \u2022 ${factor.name}: ${impactSign}${factor.impact} - ${factor.description}
`;
    });
    result += "\n";
  }
  if (output.recommendations && output.recommendations.length > 0) {
    result += `  Recommendations:
`;
    output.recommendations.forEach((rec, i) => {
      let priorityIcon = "\u{1F535}";
      const prio = rec.priority;
      if (prio === "high" /* High */ || prio === "high")
        priorityIcon = "\u{1F534}";
      else if (prio === "medium" /* Medium */ || prio === "medium")
        priorityIcon = "\u{1F7E1}";
      result += `    ${i + 1}. ${priorityIcon} ${rec.action}
`;
      result += `       Impact: +${rec.estimatedImpact} points

`;
    });
  }
  return result;
}

export {
  Severity,
  SeveritySchema,
  ToolName,
  ToolNameSchema,
  FRIENDLY_TOOL_NAMES,
  IssueType,
  IssueTypeSchema,
  AnalysisStatus,
  AnalysisStatusSchema,
  ModelTier,
  ModelTierSchema,
  LocationSchema,
  IssueSchema,
  MetricsSchema,
  AnalysisResultSchema,
  SpokeSummarySchema,
  SpokeOutputSchema,
  UnifiedReportSchema,
  AIReadyConfigSchema,
  LeadSource,
  LeadSourceSchema,
  LeadSchema,
  LeadSubmissionSchema,
  ManagedAccountSchema,
  GLOBAL_INFRA_OPTIONS,
  GLOBAL_SCAN_OPTIONS,
  COMMON_FINE_TUNING_OPTIONS,
  generateHTML,
  ReadinessRating,
  getRatingMetadata,
  getRatingLabel,
  getRatingSlug,
  getRatingEmoji,
  getToolEmoji,
  getPriorityIcon,
  getRating,
  RecommendationPriority,
  DEFAULT_TOOL_WEIGHTS,
  TOOL_NAME_MAP,
  ScoringProfile,
  SCORING_PROFILES,
  CONTEXT_TIER_THRESHOLDS,
  SIZE_ADJUSTED_THRESHOLDS,
  getProjectSizeTier,
  getRecommendedThreshold,
  normalizeToolName,
  getToolWeight,
  parseWeightString,
  calculateOverallScore,
  getRatingWithContext,
  getRatingDisplay,
  formatScore,
  formatToolScore
};
